/**
 * CheckEditor.tsx — Synthetic check create/edit form
 *
 * Purpose:    Inline form for creating or editing a scripted HTTP/browser
 *             synthetic check. Validates URL format and requires at least
 *             one geo region before saving.
 * Inputs:     initial?: Partial<SyntheticCheck>, onSave, onCancel
 * Outputs:    Controlled form; calls onSave with validated check data
 * Constraints: URL validation is client-side (new URL()); server re-validates
 */
import { useState } from 'react'
import type {
  SyntheticCheck,
  CheckScriptType,
  GeoRegion,
} from '../../hooks/useSyntheticData'

const ALL_REGIONS: GeoRegion[] = [
  'us-east',
  'us-west',
  'eu-west',
  'ap-southeast',
  'ap-northeast',
]

const REGION_LABELS: Record<GeoRegion, string> = {
  'us-east': 'US East',
  'us-west': 'US West',
  'eu-west': 'EU West',
  'ap-southeast': 'AP SE',
  'ap-northeast': 'AP NE',
}

export { REGION_LABELS, ALL_REGIONS }

export function CheckEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Partial<SyntheticCheck>
  onSave: (c: Omit<SyntheticCheck, 'id' | 'createdAt'> & { id?: string }) => Promise<boolean>
  onCancel: () => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [url, setUrl] = useState(initial?.url ?? '')
  const [scriptType, setScriptType] = useState<CheckScriptType>(initial?.scriptType ?? 'http')
  const [regions, setRegions] = useState<GeoRegion[]>(initial?.geoRegions ?? ['us-east'])
  const [interval, setInterval] = useState(String(initial?.intervalMinutes ?? 5))
  const [enabled, setEnabled] = useState(initial?.enabled ?? true)
  const [saving, setSaving] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const toggleRegion = (r: GeoRegion) => {
    setRegions((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r],
    )
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) { setValidationError('Name is required'); return }
    try { new URL(url) } catch { setValidationError('Invalid URL format'); return }
    if (regions.length === 0) { setValidationError('Select at least one geo region'); return }
    const mins = Number(interval)
    if (isNaN(mins) || mins < 1) { setValidationError('Interval must be at least 1 minute'); return }
    setValidationError(null)
    setSaving(true)
    await onSave({ id: initial?.id, name, url, scriptType, geoRegions: regions, intervalMinutes: mins, enabled })
    setSaving(false)
    onCancel()
  }

  return (
    <form onSubmit={(e) => { void handleSave(e) }} className="rounded-lg border border-sky-200 dark:border-sky-800/50 bg-brand-surface/80 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-brand-muted">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Homepage check"
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-brand-muted">Script type</label>
          <select
            value={scriptType}
            onChange={(e) => setScriptType(e.target.value as CheckScriptType)}
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-2 py-1.5 text-sm text-brand-ink focus:border-sky-500 focus:outline-none"
          >
            <option value="http">HTTP</option>
            <option value="browser">Browser</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs text-brand-muted">URL</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://api.example.com/health"
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-brand-muted">Interval (minutes)</label>
          <input
            type="number"
            min={1}
            value={interval}
            onChange={(e) => setInterval(e.target.value)}
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink focus:border-sky-500 focus:outline-none"
          />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-brand-secondary">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="rounded"
            />
            Enabled
          </label>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-2 block text-xs text-brand-muted">Geo regions</label>
          <div className="flex flex-wrap gap-2">
            {ALL_REGIONS.map((r) => (
              <label key={r} className="flex cursor-pointer items-center gap-1.5 rounded border border-brand-card px-2 py-1 text-xs text-brand-secondary hover:bg-brand-surface-2">
                <input
                  type="checkbox"
                  checked={regions.includes(r)}
                  onChange={() => toggleRegion(r)}
                  className="rounded"
                />
                {REGION_LABELS[r]}
              </label>
            ))}
          </div>
        </div>
      </div>
      {validationError && <p className="mt-2 text-xs text-red-700 dark:text-red-400">{validationError}</p>}
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={saving} className="rounded-md bg-sky-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save check'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-brand-card px-4 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2">
          Cancel
        </button>
      </div>
    </form>
  )
}
